import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { UserPlus, Check, X, Users, CalendarClock, Coffee, MapPin, Link2, UsersRound, GraduationCap, Eye, EyeOff, Copy, Home, Sparkles, Footprints, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { FoundingBadge } from "@/components/FoundingBadge";
import { InnovatorBadge } from "@/components/InnovatorBadge";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const TODAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][new Date().getDay()];
const DEFAULT_DAY = DAYS.includes(TODAY_SHORT) ? TODAY_SHORT : "Mon";

export default function Friends() {
  const { user, checkAuth } = useAuth();
  const emailDomain = user?.school_short
    ? `${user.school_short.toLowerCase()}.edu`
    : (user?.email?.split("@")[1] || "tamu.edu");
  const [email, setEmail] = useState("");
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [active, setActive] = useState(null);
  const [day, setDay] = useState("Mon");
  const [meetup, setMeetup] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [groupSel, setGroupSel] = useState([]);
  const [groupDay, setGroupDay] = useState("Mon");
  const [groupResult, setGroupResult] = useState(null);
  const [classmates, setClassmates] = useState([]);
  const [neighbors, setNeighbors] = useState(null);
  const [suggestDay, setSuggestDay] = useState(DEFAULT_DAY);
  const [suggestions, setSuggestions] = useState([]);
  const [meetupInbox, setMeetupInbox] = useState([]);
  const [sentSuggest, setSentSuggest] = useState(new Set());

  const load = useCallback(() => {
    api.get("/friends").then((r) => setFriends(r.data));
    api.get("/friends/requests").then((r) => setRequests(r.data));
    api.get("/friends/classmates").then((r) => setClassmates(r.data)).catch(() => {});
    api.get("/friends/neighbors").then((r) => setNeighbors(r.data)).catch(() => {});
    api.get("/friends/meetup-inbox").then((r) => setMeetupInbox(r.data.suggestions || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get(`/friends/meetup-suggestions?day=${suggestDay}`)
      .then((r) => setSuggestions(r.data.suggestions || []))
      .catch(() => setSuggestions([]));
  }, [suggestDay, friends]);

  const suggestToFriend = async (s) => {
    try {
      await api.post("/friends/meetup-suggest", {
        friend_id: s.friend_id, day: suggestDay, start: s.start, end: s.end,
        place_name: s.place?.name || "",
      });
      setSentSuggest((prev) => new Set(prev).add(`${s.friend_id}:${suggestDay}`));
      toast.success(`Meet-up sent to ${s.friend_name.split(" ")[0]}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
  };
  const dismissInbox = async (id) => {
    try {
      await api.post(`/friends/meetup-inbox/${id}/dismiss`);
      setMeetupInbox((list) => list.filter((x) => x.id !== id));
    } catch { toast.error("Could not dismiss"); }
  };

  useEffect(() => {
    if (!active) return;
    api.get(`/friends/${active.user_id}/meetup?day=${day}`).then((r) => setMeetup(r.data));
    api.get(`/friends/${active.user_id}/schedule?day=${day}`).then((r) => setSchedule(r.data));
  }, [active, day]);

  const toggleDiscoverable = async () => {
    try {
      await api.put("/me/discoverable", { discoverable: !user?.discoverable });
      await checkAuth();
    } catch {
      toast.error("Could not update your privacy setting");
    }
  };

  const createInvite = async () => {
    try {
      const res = await api.post("/friends/invite");
      const url = `${window.location.origin}/invite/${res.data.code}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("One-time invite link copied — share it with a friend");
      } catch {
        toast.success(url);
      }
    } catch {
      toast.error("Could not create invite link");
    }
  };

  const toggleGroup = (uid) =>
    setGroupSel((s) => (s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]));

  const findGroup = async () => {
    if (groupSel.length === 0) { toast.error("Pick at least one friend"); return; }
    const r = await api.post("/friends/group/overlap", { friend_ids: groupSel, day: groupDay });
    setGroupResult(r.data);
  };

  const sendRequest = async () => {
    if (!email.trim()) return;
    try {
      await api.post("/friends/request", { email });
      toast.success("Request sent");
      setEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    }
  };

  const respond = async (request_id, accept) => {
    await api.post("/friends/respond", { request_id, accept });
    toast.success(accept ? "Friend added" : "Declined");
    load();
  };

  const addClassmate = async (c) => {
    try {
      await api.post("/friends/request", { email: c.email });
      toast.success(`Request sent to ${c.name}`);
      setClassmates((list) => list.filter((x) => x.user_id !== c.user_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    }
  };

  const connectNeighbor = async (n) => {
    try {
      await api.post("/friends/neighbor-request", { user_id: n.user_id });
      toast.success(`Request sent to ${n.first_name}`);
      setNeighbors((d) => d ? { ...d, neighbors: d.neighbors.filter((x) => x.user_id !== n.user_id) } : d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    }
  };

  const shareAvailability = async () => {
    try {
      const res = await api.post("/share");
      const url = `${window.location.origin}/s/${res.data.share_id}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Availability link copied to clipboard");
      } catch {
        toast.success(url);
      }
    } catch {
      toast.error("Could not create link");
    }
  };

  return (
    <AppShell>
      <div className="px-4 lg:px-6 py-5">
        <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Meet up on campus</div>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5 pr-20 lg:pr-24">
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Friends</h1>
          <button
            data-testid="share-availability-btn"
            onClick={shareAvailability}
            className="inline-flex items-center gap-2 self-start bg-white border border-border rounded-md px-4 py-2.5 text-sm font-semibold hover:border-brand hover:-translate-y-0.5 transition-transform"
          >
            <Link2 size={16} strokeWidth={1.5} className="text-brand" />
            Share my availability
          </button>
        </div>

        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          {/* Left: privacy + add + list + requests */}
          <div className="col-span-12 lg:col-span-4 space-y-4">
            {meetupInbox.length > 0 && (
              <div className="space-y-2" data-testid="meetup-inbox">
                {meetupInbox.map((m) => (
                  <div key={m.id} data-testid={`meetup-inbox-${m.id}`}
                    className="rounded-xl border border-brand/30 bg-brand/[0.05] p-4">
                    <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-brand mb-1">
                      <Sparkles size={12} strokeWidth={1.5} /> Meet-up idea
                    </div>
                    <p className="text-sm">
                      <b>{m.from_name}</b> wants to meet up <b>{m.day}</b> · {m.start}–{m.end}
                      {m.place_name ? <> at <b>{m.place_name}</b></> : null}
                    </p>
                    <button data-testid={`meetup-inbox-dismiss-${m.id}`} onClick={() => dismissInbox(m.id)}
                      className="mt-2 text-xs font-semibold text-muted-foreground hover:text-foreground">
                      Dismiss
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-white border border-border rounded-xl p-5" data-testid="privacy-card">
              <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-brand mb-3">
                {user?.discoverable ? <Eye size={12} strokeWidth={1.5} /> : <EyeOff size={12} strokeWidth={1.5} />} Privacy
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Discoverable by classmates</div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {user?.discoverable
                      ? "Students on your campus can find you by email and in shared-course suggestions."
                      : "You're hidden. Others can only add you with an invite link you share."}
                  </p>
                </div>
                <button
                  data-testid="discoverable-toggle"
                  role="switch"
                  aria-checked={!!user?.discoverable}
                  onClick={toggleDiscoverable}
                  className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${user?.discoverable ? "bg-brand" : "bg-border"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${user?.discoverable ? "translate-x-5" : ""}`} />
                </button>
              </div>
            </div>

            <div className="bg-white border border-border rounded-xl p-5">
              <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand mb-2">Add a friend</div>
              <button
                data-testid="invite-link-btn"
                onClick={createInvite}
                className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-colors mb-3"
              >
                <Copy size={15} strokeWidth={1.5} />
                Copy one-time invite link
              </button>
              <p className="text-xs text-muted-foreground mb-3">
                Safest way to connect — send the link to someone you know. It works once and expires in 7 days.
              </p>
              <div className="mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground mb-2">Or search by email</div>
              <div className="flex gap-2">
                <input
                  data-testid="friend-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendRequest()}
                  placeholder={`their@${emailDomain}`}
                  className="flex-1 bg-white border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                />
                <button
                  data-testid="friend-add-btn"
                  onClick={sendRequest}
                  className="bg-brand text-white rounded-md px-3.5 hover:opacity-90 transition-colors"
                >
                  <UserPlus size={16} strokeWidth={1.5} />
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Only finds students who turned on Discoverable.</p>
            </div>

            {friends.length > 0 && (
              <div className="bg-white border border-border rounded-xl p-5" data-testid="meetup-suggestions-panel">
                <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-brand mb-1">
                  <Sparkles size={12} strokeWidth={1.5} /> Meet-up ideas
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  When you and a friend both have a break — and a spot that's close to you both.
                </p>
                <div className="flex gap-1.5 overflow-x-auto hide-scrollbar mb-3">
                  {DAYS.map((d) => (
                    <button key={d} data-testid={`suggest-day-${d}`} onClick={() => setSuggestDay(d)}
                      className={`px-2.5 py-1 rounded text-xs font-bold mono shrink-0 flex items-center gap-1 ${suggestDay === d ? "bg-brand text-white" : "bg-secondary"}`}>
                      {d}{d === TODAY_SHORT && <span className={`w-1.5 h-1.5 rounded-full ${suggestDay === d ? "bg-white" : "bg-brand"}`} />}
                    </button>
                  ))}
                </div>
                {suggestions.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-2" data-testid="suggestions-empty">
                    No mutual breaks with friends on {suggestDay}. Try another day.
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {suggestions.map((s) => (
                      <div key={s.friend_id} data-testid={`suggestion-${s.friend_id}`}
                        className="border border-border rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{s.friend_name}</div>
                            <div className="mono text-[11px] text-[#166534]">{s.duration_min}m free · {s.start}–{s.end}</div>
                          </div>
                          <button data-testid={`suggest-send-${s.friend_id}`}
                            disabled={sentSuggest.has(`${s.friend_id}:${suggestDay}`)}
                            onClick={() => suggestToFriend(s)}
                            className="flex items-center gap-1 text-xs font-semibold bg-brand text-white rounded-lg px-2.5 py-1.5 hover:opacity-90 transition-opacity shrink-0 disabled:opacity-50">
                            {sentSuggest.has(`${s.friend_id}:${suggestDay}`) ? <Check size={13} strokeWidth={2} /> : <Send size={13} strokeWidth={1.8} />}
                            {sentSuggest.has(`${s.friend_id}:${suggestDay}`) ? "Sent" : "Suggest"}
                          </button>
                        </div>
                        {s.place && (
                          <div className="mt-2 flex items-center gap-1.5 bg-secondary rounded-md px-2.5 py-1.5 text-xs" data-testid={`suggestion-place-${s.friend_id}`}>
                            {s.place.type === "dining" ? <Coffee size={13} strokeWidth={1.5} className="text-[#F59E0B]" /> : <MapPin size={13} strokeWidth={1.5} className="text-brand" />}
                            <span className="font-semibold truncate">{s.place.name}</span>
                            <span className="mono text-[11px] text-muted-foreground ml-auto flex items-center gap-1 shrink-0">
                              <Footprints size={11} strokeWidth={1.8} /> {s.place.walk_you_min}m / {s.place.walk_friend_min}m
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {neighbors && !neighbors.no_home && neighbors.neighbors.length > 0 && (
              <div className="bg-white border border-border rounded-xl p-5" data-testid="neighbors-panel">
                <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-brand mb-1">
                  <Home size={12} strokeWidth={1.5} /> Neighbors at {neighbors.complex}
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Classmates who set the same home — great for carpools & study buddies.
                </p>
                <div className="space-y-2.5">
                  {neighbors.neighbors.map((n) => (
                    <div key={n.user_id} data-testid={`neighbor-${n.user_id}`} className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-brand/10 text-brand flex items-center justify-center text-xs font-bold shrink-0">
                        {n.first_name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                          {n.first_name}
                          {n.founding_member && <FoundingBadge compact />}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {n.distance_m === 0 ? n.complex : `~${n.distance_m}m away`}
                        </div>
                      </div>
                      <button
                        data-testid={`neighbor-connect-${n.user_id}`}
                        onClick={() => connectNeighbor(n)}
                        aria-label={`Connect with ${n.first_name}`}
                        title={`Connect with ${n.first_name}`}
                        className="p-1.5 rounded-md bg-brand text-white hover:opacity-90 transition-colors shrink-0"
                      >
                        <UserPlus size={15} strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {neighbors && !neighbors.no_home && neighbors.me_discoverable && neighbors.neighbors.length === 0 && (
              <div className="bg-white border border-border rounded-xl p-4 text-xs text-muted-foreground" data-testid="neighbors-empty">
                No neighbors yet at {neighbors.complex || "your complex"}. As more students set the same home, they'll show up here for carpools & study groups.
              </div>
            )}

            {neighbors && !neighbors.no_home && neighbors.neighbors.length === 0 && !neighbors.me_discoverable && (
              <div className="bg-white border border-border rounded-xl p-4 text-xs text-muted-foreground" data-testid="neighbors-optin-hint">
                Turn on <b>Discoverable</b> above so neighbors at {neighbors.complex || "your complex"} can find you for carpools & study groups.
              </div>
            )}

            {classmates.length > 0 && (
              <div className="bg-white border border-border rounded-xl p-5" data-testid="classmates-panel">
                <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-brand mb-1">
                  <GraduationCap size={12} strokeWidth={1.5} /> Classmates in your courses
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Students on your campus taking the same classes.
                </p>
                <div className="space-y-2.5">
                  {classmates.map((c) => (
                    <div key={c.user_id} data-testid={`classmate-${c.user_id}`} className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-brand/10 text-brand flex items-center justify-center text-xs font-bold shrink-0">
                        {c.name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{c.name}</div>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {c.shared_courses.map((code) => (
                            <span key={code} className="mono text-[11px] font-bold bg-secondary text-foreground rounded px-1.5 py-0.5">
                              {code}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button
                        data-testid={`classmate-add-${c.user_id}`}
                        onClick={() => addClassmate(c)}
                        title={`Add ${c.name}`}
                        className="p-1.5 rounded-md bg-brand text-white hover:opacity-90 transition-colors shrink-0"
                      >
                        <UserPlus size={15} strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {requests.length > 0 && (
              <div className="bg-white border border-border rounded-xl p-5" data-testid="requests-panel">
                <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand mb-3">
                  Requests ({requests.length})
                </div>
                <div className="space-y-2">
                  {requests.map((r) => (
                    <div key={r.request_id} className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-xs font-bold">
                        {r.name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{r.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                      </div>
                      <button data-testid={`accept-${r.request_id}`} onClick={() => respond(r.request_id, true)}
                        className="p-1.5 rounded-md bg-[#16A34A]/10 text-[#16A34A] hover:bg-[#16A34A]/20 transition-colors">
                        <Check size={15} strokeWidth={2} />
                      </button>
                      <button data-testid={`decline-${r.request_id}`} onClick={() => respond(r.request_id, false)}
                        className="p-1.5 rounded-md hover:bg-destructive/10 hover:text-destructive transition-colors">
                        <X size={15} strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <div className="p-5 border-b border-border mono text-[11px] tracking-[0.2em] uppercase text-brand">
                Your friends ({friends.length})
              </div>
              {friends.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  <Users size={20} className="mx-auto mb-2 text-muted-foreground" strokeWidth={1.5} />
                  Add friends to find overlapping free time.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {friends.map((f) => (
                    <button
                      key={f.user_id}
                      data-testid={`friend-${f.user_id}`}
                      onClick={() => setActive(f)}
                      className={`w-full flex items-center gap-3 p-4 text-left transition-colors ${
                        active?.user_id === f.user_id ? "bg-brand/5" : "hover:bg-secondary"
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">
                        {f.name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate flex items-center gap-1.5">
                          <span className="truncate">{f.name}</span>
                          {f.founding_member && <FoundingBadge compact />}
                          {f.innovator && <InnovatorBadge compact />}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{f.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Study-group finder */}
            {friends.length > 0 && (
              <div className="bg-white border border-border rounded-xl p-5" data-testid="group-finder">
                <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase text-brand mb-3">
                  <UsersRound size={12} strokeWidth={1.5} /> Study-group finder
                </div>
                <div className="space-y-1.5 mb-3">
                  {friends.map((f) => (
                    <label key={f.user_id} data-testid={`group-pick-${f.user_id}`} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={groupSel.includes(f.user_id)}
                        onChange={() => toggleGroup(f.user_id)}
                        className="accent-brand w-4 h-4"
                      />
                      {f.name}
                    </label>
                  ))}
                </div>
                <div className="flex gap-1.5 overflow-x-auto hide-scrollbar mb-3">
                  {DAYS.map((d) => (
                    <button key={d} data-testid={`group-day-${d}`} onClick={() => setGroupDay(d)}
                      className={`px-2.5 py-1 rounded text-xs font-bold mono shrink-0 ${groupDay === d ? "bg-brand text-white" : "bg-secondary"}`}>{d}</button>
                  ))}
                </div>
                <button data-testid="group-find-btn" onClick={findGroup}
                  className="w-full bg-brand text-white rounded-md py-2.5 text-sm font-semibold hover:opacity-90 transition-colors">
                  Find shared free time
                </button>
                {groupResult && (
                  <div className="mt-3 space-y-2" data-testid="group-result">
                    <div className="text-xs text-muted-foreground">
                      {groupResult.people.join(", ")} · {groupResult.day}
                    </div>
                    {groupResult.overlaps.length === 0 ? (
                      <div className="text-sm text-muted-foreground">No time when everyone is free on {groupResult.day}.</div>
                    ) : (
                      groupResult.overlaps.map((o, i) => (
                        <div key={i} className="flex items-center justify-between bg-[#16A34A]/10 rounded-md px-3 py-2">
                          <span className="mono text-sm font-bold text-[#166534]">{o.duration_min}m</span>
                          <span className="mono text-xs text-[#166534]">{o.start} – {o.end}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: meetup finder */}
          <div className="col-span-12 lg:col-span-8">
            {!active ? (
              <div className="bg-white border border-border rounded-xl p-10 text-center h-full flex flex-col items-center justify-center">
                <CalendarClock size={28} strokeWidth={1.5} className="text-brand mb-3" />
                <h3 className="font-display text-xl font-semibold">Pick a friend</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                  Select someone to see when you both have a break — and where to meet.
                </p>
              </div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                <div className="bg-white border border-border rounded-xl p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Meet up with</div>
                      <h3 className="font-display text-2xl font-semibold">{active.name}</h3>
                    </div>
                    <div className="flex gap-1.5 overflow-x-auto hide-scrollbar">
                      {DAYS.map((d) => (
                        <button key={d} data-testid={`meetup-day-${d}`} onClick={() => setDay(d)}
                          className={`px-3 py-1.5 rounded-md text-xs font-bold mono shrink-0 transition-colors ${
                            day === d ? "bg-brand text-white" : "bg-secondary hover:bg-secondary/70"
                          }`}>
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-border rounded-xl p-5" data-testid="meetup-results">
                  <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand mb-3">
                    Shared free time · {day}
                  </div>
                  {!meetup || meetup.overlaps.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-4">
                      No overlapping breaks on {day}. Try another day.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {meetup.overlaps.map((o, i) => (
                        <div key={i} className="border border-border rounded-lg p-4" data-testid={`overlap-${i}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-baseline gap-2">
                              <span className="font-display text-xl font-semibold mono">{o.duration_min} min</span>
                              <span className="text-sm text-muted-foreground mono">{o.start} – {o.end}</span>
                            </div>
                            <span className="bg-[#16A34A]/10 text-[#16A34A] mono text-[11px] font-bold px-2 py-1 rounded uppercase">
                              Both free
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {o.suggestions.map((s) => (
                              <div key={s.id} className="flex items-center gap-1.5 bg-secondary rounded-md px-3 py-1.5 text-xs font-semibold">
                                {s.type === "dining" ? <Coffee size={13} strokeWidth={1.5} className="text-[#F59E0B]" /> : <MapPin size={13} strokeWidth={1.5} className="text-brand" />}
                                {s.name}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {schedule && (
                  <div className="bg-white border border-border rounded-xl p-5">
                    <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand mb-3">
                      {active.name}'s {day} classes
                    </div>
                    {schedule.classes.length === 0 ? (
                      <div className="text-sm text-muted-foreground">No classes on {day}.</div>
                    ) : (
                      <div className="divide-y divide-border">
                        {schedule.classes.map((c) => (
                          <div key={c.id} className="flex items-center gap-3 py-2.5">
                            <span className="mono text-xs font-bold text-brand w-24">{c.start_time}–{c.end_time}</span>
                            <span className="font-semibold text-sm">{c.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
